import React, { Suspense, lazy } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { isEmpty, find, isNull } from 'lodash-es'
import { Switch, Route, withRouter } from 'react-router-dom'
import { getBodyClassFromPath, sentryReportError } from '../shared/utils'
import { SiteContext, SettingsContext } from '../shared/context'
import {
  Notification,
  NotificationComponent,
  CriticalErrorComponent,
} from '../shared/components'

import '../shared/style/style.scss'
import 'antd/lib/notification/style/css'
import {
  initApp as initAppAction,
  leaveApp as leaveAppAction,
  resetCriticalError as resetCriticalErrorAction,
} from './actions'

import { PvSnackbarRootComponent } from './Personal/components'

import './App.scss'

const coreRoutes = [
  '/inbound',
  '/settings',
  '/manage/video/:photo_id(\\d+)',
  '/connect',
  '/tools',
]
const webinarProducerRoutes = [
  '/manage/webinar/:live_id(\\d+)/:tab_key?',
  '/manage/webinars',
  '/manage/sidebar',
  '/webinar',
]

const CoreRoutesComponent = lazy(() =>
  import(/* webpackChunkName: 'core' */ './routes/CoreRoutesComponent')
)
const PersonalRoutesComponent = lazy(() =>
  import(/* webpackChunkName: 'personal' */ './routes/PersonalRoutesComponent')
)
const AudienceRoutesComponent = lazy(() =>
  import(/* webpackChunkName: 'audience' */ './routes/AudienceRoutesComponent')
)
const AnalyticsRoutesComponent = lazy(() =>
  import(
    /* webpackChunkName: 'analytics' */ './routes/AnalyticsRoutesComponent'
  )
)
const LiveRoutesComponent = lazy(() =>
  import(/* webpackChunkName: 'live' */ './routes/LiveRoutesComponent')
)
const WebinarProducerRoutesComponent = lazy(() =>
  import(
    /* webpackChunkName: 'webinar-producer' */ './routes/WebinarProducerRoutesComponent'
  )
)
const WebinarAudienceRoomRoutesComponent = lazy(() =>
  import(
    /* webpackChunkName: 'webinar-audience-room' */ './routes/WebinarAudienceRoomRoutesComponent'
  )
)

class App extends React.Component {
  constructor(props) {
    super(props)

    this.body = document.body

    const bodyClass = getBodyClassFromPath(props.location.pathname)
    this.state = { bodyClass }
    this.setBodyClass(bodyClass)
  }

  componentDidMount() {
    const { initApp } = this.props

    initApp()
  }

  componentWillReceiveProps(nextProps) {
    const { bodyClass } = this.state

    const bodyClassFromPath = getBodyClassFromPath(nextProps.location.pathname)
    if (bodyClassFromPath !== bodyClass) {
      this.setState({ bodyClass: bodyClassFromPath })
      this.setBodyClass(bodyClassFromPath)
    }
  }

  componentDidCatch(error, errorInfo) {
    sentryReportError(error, errorInfo)
  }

  componentWillUnmount() {
    const { leaveApp } = this.props

    leaveApp()
  }

  setBodyClass(bodyClass) {
    if (isEmpty(bodyClass)) {
      return
    }
    const reactClass = find(
      this.body.className.split(' '),
      (className) => className.indexOf('react-') > -1
    )
    this.body.classList.remove(reactClass)
    this.body.classList.add(bodyClass)
  }

  renderCriticalError() {
    const { criticalErrorMessage, resetCriticalError, site } = this.props

    return (
      <CriticalErrorComponent
        message={criticalErrorMessage}
        site={site}
        resetCriticalError={resetCriticalError}
      />
    )
  }

  static renderRoutes() {
    return (
      <Suspense fallback={null}>
        <Switch>
          <Route path="/analytics" component={AnalyticsRoutesComponent} />
          <Route path="/audience" component={AudienceRoutesComponent} />
          <Route path="/personal" component={PersonalRoutesComponent} />
          <Route
            path="/manage/live/:live_id(\d+)"
            component={LiveRoutesComponent}
          />
          <Route path={coreRoutes} component={CoreRoutesComponent} />
          <Route
            path={webinarProducerRoutes}
            component={WebinarProducerRoutesComponent}
          />
          <Route component={WebinarAudienceRoomRoutesComponent} />
        </Switch>
      </Suspense>
    )
  }

  render() {
    const { site, settings, criticalErrorMessage } = this.props
    if (!site.site_id) {
      return null
    }

    const isPersonal = site.product_key === 'personal'

    return (
      <div className="admin-root">
        <SiteContext.Provider value={site}>
          <SettingsContext.Provider value={settings}>
            {isNull(criticalErrorMessage)
              ? App.renderRoutes()
              : this.renderCriticalError()}
            {isPersonal ? <PvSnackbarRootComponent /> : <Notification />}
            <NotificationComponent
              theme={site.product_key === 'personal' ? 'personal' : 'vmp'}
            />
          </SettingsContext.Provider>
        </SiteContext.Provider>
      </div>
    )
  }
}

App.propTypes = {
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  site: PropTypes.object,
  initApp: PropTypes.func,
  leaveApp: PropTypes.func,
  resetCriticalError: PropTypes.func,
  criticalErrorMessage: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  settings: PropTypes.arrayOf(PropTypes.object),
}

const mapStateToProps = (state) => ({
  site: state.site.siteData,
  settings: state.site.siteSettings,
  criticalErrorMessage: state.site.criticalErrorMessage,
})

export default withRouter(
  connect(mapStateToProps, {
    initApp: initAppAction,
    leaveApp: leaveAppAction,
    resetCriticalError: resetCriticalErrorAction,
  })(App)
)
export { App as AppComponent }
